import { useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import type { MatchRequestResultItem } from "../../config/api";
import { EMPTY_VALUE } from "../../i18n/format";
import { locationLabel } from "../../lib/regionOptions";
import { trustBadgeLabel, trustBadgeVariant } from "../../lib/trustLabels";
import type { MatchRequest } from "../../services/match-requests/generated/model";
import MatchExplanation from "../MatchExplanation/MatchExplanation";
import CompanyProfileModal from "./CompanyProfileModal";
import "./MatchResultsModal.css";

interface MatchResultsModalProps {
  isOpen: boolean;
  onClose: () => void;
  request: MatchRequest & { results?: MatchRequestResultItem[] };
  dateCreated: string;
  onInitiateContact?: (matchResultId: number) => void;
}

const MatchResultsModal = ({ isOpen, onClose, request, dateCreated, onInitiateContact }: MatchResultsModalProps) => {
  const { t } = useTranslation(["b2b", "common"]);
  const [profileResult, setProfileResult] = useState<MatchRequestResultItem | null>(null);

  if (!isOpen) return null;

  const results = (request.results as unknown as MatchRequestResultItem[]) ?? [];
  const totalMatches = results.length;
  const avgMatchScore =
    results.length > 0 ? Math.round(results.reduce((sum, r) => sum + r.winWinMatchScore, 0) / results.length) : 0;
  const highConfidence = results.filter((r) => r.winWinMatchScore >= 90).length;
  const productService = request.productServiceName;
  const hsCode = request.hsCode;
  const requestTitle = request.requirementTitle;

  return (
    <div className="match-results-modal__overlay" onClick={onClose}>
      <div className="match-results-modal__content" onClick={(e) => e.stopPropagation()}>
        <button className="match-results-modal__close" onClick={onClose} aria-label={t("common:actions.close")}>
          <i className="fa-solid fa-xmark" />
        </button>

        <div className="match-results-modal__header">
          <h2 className="match-results-modal__title">{t("matchResultsModal.title")}</h2>
          <p className="match-results-modal__subtitle">{requestTitle}</p>
        </div>

        <div className="match-results-modal__request-details">
          <div className="match-results-modal__detail-item">
            <span className="match-results-modal__detail-label">{t("matchResultsModal.details.productService")}</span>
            <span className="match-results-modal__detail-value">{productService}</span>
          </div>
          <div className="match-results-modal__detail-item">
            <span className="match-results-modal__detail-label">{t("matchResultsModal.details.hsCode")}</span>
            <span className="match-results-modal__detail-value">{hsCode}</span>
          </div>
          <div className="match-results-modal__detail-item">
            <span className="match-results-modal__detail-label">{t("matchResultsModal.details.dateCreated")}</span>
            <span className="match-results-modal__detail-value">{dateCreated}</span>
          </div>
        </div>

        <div className="match-results-modal__divider" />

        <div className="match-results-modal__stats">
          <div className="match-results-modal__stat-item">
            <span className="match-results-modal__stat-label">{t("matchResultsModal.stats.totalMatches")}</span>
            <span className="match-results-modal__stat-value match-results-modal__stat-value--blue">
              {totalMatches}
            </span>
          </div>
          <div className="match-results-modal__stat-item">
            <span className="match-results-modal__stat-label">{t("matchResultsModal.stats.avgScore")}</span>
            <span className="match-results-modal__stat-value match-results-modal__stat-value--green">
              {avgMatchScore}%
            </span>
          </div>
          <div className="match-results-modal__stat-item">
            <span className="match-results-modal__stat-label">{t("matchResultsModal.stats.highConfidence")}</span>
            <span className="match-results-modal__stat-value match-results-modal__stat-value--purple">
              {highConfidence}
            </span>
          </div>
        </div>

        <div className="match-results-modal__divider" />

        <div className="match-results-modal__results">
          {results.length === 0 ? (
            <p className="match-results-modal__empty">
              {request.status === "failed"
                ? request.message || t("matchResultsModal.failed")
                : t("matchResultsModal.empty")}
            </p>
          ) : (
            results.map((result, index) => {
              const rank = index + 1;
              const company = result.company;
              const location = locationLabel(company.country, company.region);
              return (
                <div key={result.id} className="match-results-modal__result-card">
                  <div className="match-results-modal__result-header">
                    <div className="match-results-modal__result-rank">#{rank}</div>
                    <div className="match-results-modal__result-company">
                      <h3 className="match-results-modal__company-name">{company.companyName}</h3>
                      {location && (
                        <p className="match-results-modal__company-location">
                          <i className="fa-solid fa-location-dot" />
                          <span>{location}</span>
                        </p>
                      )}
                    </div>
                    <div className="match-results-modal__result-score">
                      <span className="match-results-modal__score-label">{t("matchResultsModal.card.score")}</span>
                      <span className="match-results-modal__score-value">{result.winWinMatchScore}%</span>
                      <div
                        className={`match-results-modal__trust-badge match-results-modal__trust-badge--${trustBadgeVariant(company)}`}
                      >
                        {trustBadgeLabel(company)}
                      </div>
                    </div>
                  </div>

                  <div className="match-results-modal__matched-product">
                    <span className="match-results-modal__matched-label">
                      {t("matchResultsModal.card.matchedItem")}
                    </span>
                    <p className="match-results-modal__matched-name">{result.matchedItem?.name || EMPTY_VALUE}</p>
                    {result.matchedItem?.hsCode && (
                      <p className="match-results-modal__matched-hs">
                        {t("matchResultsModal.card.hsCode", { code: result.matchedItem.hsCode })}
                      </p>
                    )}
                  </div>

                  <div className="match-results-modal__ahp-breakdown">
                    <span className="match-results-modal__ahp-label">{t("matchResultsModal.card.ahpBreakdown")}</span>
                    <div className="match-results-modal__criteria-list">
                      <div className="match-results-modal__criterion">
                        <span className="match-results-modal__criterion-name">
                          {t("matchResultsModal.card.industryMatch")}
                        </span>
                        <div className="match-results-modal__criterion-bar-wrapper">
                          <div
                            className="match-results-modal__criterion-bar"
                            style={{ width: `${result.industryMatch}%` }}
                          />
                        </div>
                        <span className="match-results-modal__criterion-value">{result.industryMatch}%</span>
                      </div>
                      <div className="match-results-modal__criterion">
                        <span className="match-results-modal__criterion-name">
                          {t("matchResultsModal.card.geographicLocation")}
                        </span>
                        <div className="match-results-modal__criterion-bar-wrapper">
                          <div
                            className="match-results-modal__criterion-bar"
                            style={{
                              width: `${result.geographicLocation}%`,
                            }}
                          />
                        </div>
                        <span className="match-results-modal__criterion-value">{result.geographicLocation}%</span>
                      </div>
                    </div>
                  </div>

                  <div className="match-results-modal__why">
                    <MatchExplanation lines={result.explanation} confidence={result.matchConfidence} />
                  </div>

                  <div className="match-results-modal__actions">
                    <button
                      type="button"
                      className="match-results-modal__action-button match-results-modal__action-button--secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        setProfileResult(result);
                      }}
                    >
                      {t("matchResultsModal.card.viewProfile")}
                    </button>
                    <button
                      type="button"
                      className="match-results-modal__action-button match-results-modal__action-button--primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        onInitiateContact?.(result.id);
                      }}
                    >
                      {t("matchResultsModal.card.initiateContact")}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {profileResult &&
        createPortal(
          <CompanyProfileModal
            isOpen
            onClose={() => setProfileResult(null)}
            result={profileResult}
            onInitiateContact={(matchResultId) => {
              onInitiateContact?.(matchResultId);
              setProfileResult(null);
            }}
          />,
          document.body,
        )}
    </div>
  );
};

export default MatchResultsModal;
